import { clientConditionOptions } from "./clientFormOptions";
import React, { useState, useEffect, useRef } from "react";
import { activityFromApe, companyActivity } from "../../utils/companyActivity";
import { message } from "antd";
import axios from "../../axios/AxiosInstance";
import { freelanceCommercialName } from "../../utils/freelanceCommercial";

const groups = [
  [
    "Identité",
    [
      ["denomination", "Nom du client / établissement", "text", true],
      ["legalName", "Raison sociale"],
      ["siret", "SIRET"],
      ["ape", "Code APE"],
      ["activity", "Activité principale de la société (NAF/APE)"],
      ["TVA", "TVA intracommunautaire"],
    ],
  ],
  [
    "Siège social",
    [
      ["address", "Adresse"],
      ["postCode", "Code postal"],
      ["town", "Ville"],
    ],
  ],
  [
    "Livraison",
    [
      ["addressLivraison", "Adresse"],
      ["postCodeLivraison", "Code postal"],
      ["townLivraison", "Ville"],
    ],
  ],
  [
    "Facturation",
    [
      ["addressFacturation", "Adresse"],
      ["postCodeFacturation", "Code postal"],
      ["townFacturation", "Ville"],
    ],
  ],
  [
    "Conditions et suivi commercial",
    [
      ["facturation", "Conditions de paiement"],
      ["tvaRate", "Taux de TVA"],
      ["clientAcquisition", "Mode d’acquisition"],
    ],
  ],
];

export default function EditClientInfoForm({ client, onClose, onSaved }) {
  const [form, setForm] = useState(() =>
    Object.fromEntries(
      groups.flatMap(([, fields]) =>
        fields.map(([key]) => [
          key,
          key === "denomination"
            ? client.companyName || client.denomination || ""
            : key === "ape"
              ? client.APE || client.ape || ""
              : key === "activity"
                ? companyActivity(client)
                : (client[key] ?? ""),
        ]),
      ),
    ),
  );
  const [saving, setSaving] = useState(false);
  const [lookup, setLookup] = useState({ loading: false, message: "" });
  const currentForm = useRef(form);
  currentForm.current = form;
  useEffect(() => {
    const siret = String(form.siret || "").replace(/\s/g, "");
    if (!/^\d{14}$/.test(siret)) {
      setLookup({ loading: false, message: "" });
      return;
    }
    let active = true;
    const controller = new AbortController();
    const snapshot = { ...currentForm.current };
    setLookup({ loading: true, message: "Recherche de l’entreprise…" });
    const timer = setTimeout(async () => {
      try {
        const { data } = await axios.get(`client/lookup/siret/${siret}`, {
          signal: controller.signal,
        });
        if (!active) return;
        setForm((current) => {
          if (String(current.siret).replace(/\s/g, "") !== siret)
            return current;
          const next = { ...current, siret };
          for (const key of [
            "legalName",
            "address",
            "postCode",
            "town",
            "ape",
            "activity",
            "TVA",
          ]) {
            const value = key === "legalName" ? data.denomination : data[key];
            if (value && current[key] === snapshot[key]) next[key] = value;
          }
          return next;
        });
        setLookup({
          loading: false,
          message:
            "Informations légales complétées. Le nom du client / établissement est conservé.",
        });
      } catch (error) {
        if (active)
          setLookup({
            loading: false,
            message:
              error.response?.data?.message ||
              "Recherche indisponible. Vous pouvez compléter la fiche manuellement.",
          });
      }
    }, 400);
    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [form.siret]);

  const submit = async (event) => {
    event.preventDefault();
    if (saving || lookup.loading) return;
    if (!form.denomination.trim()) {
      message.error("Renseignez le nom du client / établissement.");
      return;
    }
    setSaving(true);
    try {
      const values = Object.fromEntries(
        Object.entries(form).map(([key, value]) => [key, String(value).trim()]),
      );
      const ownName = freelanceCommercialName();
      const payload = {
        ...values,
        companyName: values.denomination,
        ...(ownName ? { commercial: ownName } : {}),
      };
      payload.APE = values.ape;
      const { data } = await axios.patch(`client/${client._id}`, payload);
      onSaved({ ...client, ...payload, ...data });
      message.success("Informations du client enregistrées.");
      onClose();
    } catch {
      message.error("Impossible d’enregistrer les modifications. Réessayez.");
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} aria-label="Modifier les informations du client">
      {lookup.message && (
        <p
          role="status"
          className="rounded-lg bg-blue-50 px-4 py-3 text-sm text-blue-800"
        >
          {lookup.message}
        </p>
      )}
      <div className="grid grid-cols-1 gap-4 py-4 lg:grid-cols-2">
        {groups.map(([title, fields]) => (
          <fieldset
            key={title}
            disabled={saving}
            className="min-w-0 rounded-xl border border-blue-200 bg-blue-50/40 p-5"
          >
            <legend className="mb-3 text-sm font-semibold text-slate-900">
              {title}
            </legend>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {fields.map(([key, label, type = "text", required = false]) => (
                <label
                  key={key}
                  htmlFor={`edit-client-${key}`}
                  className="block text-sm text-slate-600"
                >
                  {label}
                  {required && " *"}
                  {clientConditionOptions[key] ? (
                    <select
                      id={`edit-client-${key}`}
                      name={key}
                      value={form[key]}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          [key]: event.target.value,
                        }))
                      }
                      className="mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">Sélectionner…</option>
                      {form[key] &&
                        !clientConditionOptions[key].includes(form[key]) && (
                          <option value={form[key]}>{form[key]}</option>
                        )}
                      {clientConditionOptions[key].map((value) => (
                        <option key={value} value={value}>
                          {value}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      id={`edit-client-${key}`}
                      name={key}
                      type={type}
                      required={required}
                      value={form[key]}
                      onChange={(event) =>
                        setForm((current) => ({
                          ...current,
                          [key]: event.target.value,
                          ...(key === "ape"
                            ? { activity: activityFromApe(event.target.value) }
                            : {}),
                        }))
                      }
                      className="mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-100 disabled:bg-slate-50"
                    />
                  )}
                </label>
              ))}
            </div>
            {title === "Siège social" && (
              <div className="mt-4 flex flex-wrap gap-2">
                {["Livraison", "Facturation"].map((destination) => (
                  <button
                    key={destination}
                    type="button"
                    disabled={saving || lookup.loading}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        [`address${destination}`]: current.address,
                        [`postCode${destination}`]: current.postCode,
                        [`town${destination}`]: current.town,
                      }))
                    }
                    className="rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50 disabled:opacity-50"
                  >
                    Copier vers {destination}
                  </button>
                ))}
              </div>
            )}
          </fieldset>
        ))}
      </div>
      <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
        Commercial :{" "}
        {freelanceCommercialName() || client.commercial || "votre nom"}. Il est
        enregistré automatiquement avec votre nom.
      </p>
      <div className="flex justify-end gap-3 border-t border-slate-200 pt-4">
        <button
          type="button"
          disabled={saving}
          onClick={onClose}
          className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium disabled:opacity-50"
        >
          Annuler
        </button>
        <button
          type="submit"
          disabled={saving || lookup.loading}
          className="rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {saving ? "Enregistrement…" : "Enregistrer"}
        </button>
      </div>
    </form>
  );
}
